import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Youtube, Fish, FileText, Home as HomeIcon, FlaskConical, Settings, Users, Users2, Mail, ExternalLink, SlidersHorizontal, PoundSterling, Gift, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CrudManager } from "@/components/admin/CrudManager";
import { FinancePanel } from "@/components/admin/FinancePanel";
import { RESOURCES } from "@/pages/admin/adminConfig";
import { IMAGES } from "@/lib/content";
import { Switch } from "@/components/ui/switch";

const NAV = [
  { key: "videos", label: "Videos", icon: Youtube },
  { key: "breeding", label: "Breeding", icon: Fish },
  { key: "journal", label: "Journal", icon: FileText },
  { key: "fishroom", label: "Fish Room", icon: HomeIcon },
  { key: "testlab", label: "Test Lab", icon: FlaskConical },
  { key: "finance", label: "Finance", icon: PoundSterling },
  { key: "sections", label: "Sections", icon: SlidersHorizontal },
  { key: "settings", label: "Settings", icon: Settings },
  { key: "members", label: "Members", icon: Users2 },
  { key: "subscribers", label: "Subscribers", icon: Users },
  { key: "messages", label: "Messages", icon: Mail },
];

const FEATURE_LABELS = [
  ["journey", "The Journey", "Timeline page + homepage timeline section"],
  ["youtube", "Videos & YouTube", "Videos page/tab + homepage video section"],
  ["fish_room", "Fish Room", "Fish Room page + homepage gallery section"],
  ["breeding", "Breeding", "Breeding page + homepage breeding section"],
  ["business", "Business Journey", "Business page + homepage business section"],
  ["community", "Community", "Community page, newsletter & 'Join' button"],
  ["forum", "Community Forum", "The whole forum (boards, topics, member sign-up)"],
  ["test_lab", "Test Lab", "Test Lab page + homepage teaser"],
  ["journal", "Journal / Blog", "Journal listing & article pages"],
  ["recent_winners", "Recent Winners", "Homepage banner showing your public giveaway winners"],
];

const inputCls =
  "w-full bg-[#0C131D] border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-brand-textMuted focus:outline-none focus:ring-2 focus:ring-brand-cyan";

const AdvancedGiveawayPanel = () => {
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [random, setRandom] = useState(false);
  const [entrants, setEntrants] = useState([]);

  const refresh = () => api.get("/giveaway").then((r) => {
    setUsername(r.data.secret_winner || "");
    setRandom(!!r.data.random_mode);
    setEntrants(r.data.entrants || []);
  }).catch(() => {});

  useEffect(() => { refresh(); }, []);

  const norm = (s) => s.trim().toLowerCase().replace(/^@/, "").replace(/\s+/g, " ");
  const combined = username.trim();
  const matches = combined && entrants.some((e) => norm(e) === norm(combined));

  const toggleRandom = async (val) => {
    setRandom(val);
    try {
      await api.put("/giveaway", { random_mode: val });
      toast.success(val ? "Random mode ON — spins are genuinely random" : "Random mode OFF — the preferred winner will win");
    } catch {
      setRandom(!val);
      toast.error("Could not update");
    }
  };

  const saveNames = async () => {
    try {
      await api.put("/giveaway", { secret_first: "", secret_last: "", secret_winner: combined });
      toast.success(combined ? "Preferred winner saved" : "Preferred winner cleared");
      refresh();
    } catch {
      toast.error("Save failed");
    }
  };

  return (
    <div className="max-w-xl mt-10 border-t border-white/10 pt-6">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 text-brand-textSec hover:text-white text-sm" data-testid="settings-advanced-toggle">
        <ChevronDown size={16} className={`transition-transform ${open ? "" : "-rotate-90"}`} /> Advanced
      </button>
      {open && (
        <div className="mt-4 space-y-4" data-testid="settings-advanced-panel">
          <div className="flex items-center justify-between gap-4 bg-[#0C131D] border border-white/10 rounded-lg px-4 py-3">
            <div>
              <p className="text-sm text-white font-medium">Random mode</p>
              <p className="text-xs text-brand-textMuted mt-0.5">On = genuinely random. Off = the preferred winner below wins. Takes effect on the next spin.</p>
            </div>
            <Switch checked={random} onCheckedChange={toggleRandom}
              className="data-[state=checked]:bg-brand-cyan data-[state=unchecked]:bg-white/15"
              data-testid="advanced-random-toggle" />
          </div>

          <div className={random ? "opacity-50 pointer-events-none" : ""}>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-brand-textSec mb-1.5 block">Preferred winner — YouTube username</label>
                <input value={username} onChange={(e) => setUsername(e.target.value)} className={inputCls} placeholder="e.g. @fishfan123" data-testid="advanced-secret-username" />
              </div>
              {combined && entrants.length > 0 && (
                matches
                  ? <p className="text-emerald-400 text-xs" data-testid="advanced-secret-match">✓ Matches an entrant in your list.</p>
                  : <p className="text-amber-400 text-xs" data-testid="advanced-secret-warning">⚠ No entrant matches that username yet — the spin would fall back to random.</p>
              )}
              {combined && entrants.length === 0 && (
                <p className="text-brand-textMuted text-xs" data-testid="advanced-secret-noentrants">Saved. It will be checked against the entrant list once you upload it.</p>
              )}
              <button type="button" onClick={saveNames} className="btn-primary px-5 py-2.5 text-sm" data-testid="advanced-secret-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SettingsPanel = () => {
  const [form, setForm] = useState(null);
  const fields = [
    ["youtube_channel_url", "YouTube Channel URL"],
    ["youtube_subscribe_url", "YouTube Subscribe URL"],
    ["intro_video_id", "YouTube Intro Video ID (for About page)"],
    ["instagram_url", "Instagram URL"],
    ["tiktok_url", "TikTok URL"],
    ["facebook_url", "Facebook URL"],
    ["announcement", "Site Announcement"],
  ];

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch(() => setForm({}));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/settings", form);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    }
  };

  if (!form) return <p className="text-brand-textMuted">Loading…</p>;

  return (
    <div data-testid="settings-panel">
      <h2 className="font-display font-bold text-2xl text-white mb-1">Site Settings</h2>
      <p className="text-brand-textMuted text-sm mb-6">Social links and channel URLs used across the site. Leave blank to hide.</p>
      <form onSubmit={save} className="max-w-xl space-y-4">
        {fields.map(([key, label]) => (
          <div key={key}>
            <label className="text-sm text-brand-textSec mb-1.5 block">{label}</label>
            <input value={form[key] || ""} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} className={inputCls} data-testid={`setting-${key}`} placeholder="https://…" />
          </div>
        ))}
        <label className="flex items-center gap-2 cursor-pointer pt-1">
          <input type="checkbox" checked={!!form.announcement_active} onChange={(e) => setForm((f) => ({ ...f, announcement_active: e.target.checked }))} className="h-4 w-4 accent-[#25C4D8]" data-testid="setting-announcement-active" />
          <span className="text-sm text-brand-textSec">Show announcement banner</span>
        </label>
        <button type="submit" className="btn-primary px-5 py-2.5 text-sm mt-2" data-testid="settings-save-btn">Save Settings</button>
      </form>
      <AdvancedGiveawayPanel />
    </div>
  );
};

const ReadOnlyTable = ({ endpoint, columns, title, testid }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(endpoint).then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [endpoint]);
  return (
    <div data-testid={testid}>
      <h2 className="font-display font-bold text-2xl text-white mb-1">{title}</h2>
      <p className="text-brand-textMuted text-sm mb-6">{rows.length} total</p>
      {loading ? (
        <p className="text-brand-textMuted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-brand-textMuted text-sm">Nothing here yet.</p>
      ) : (
        <div className="overflow-x-auto border border-white/10 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left">
                {columns.map((c) => <th key={c.key} className="px-4 py-3 text-brand-textSec font-medium">{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-white/5 last:border-0">
                  {columns.map((c) => <td key={c.key} className="px-4 py-3 text-white/90 align-top">{c.render ? c.render(r) : (r[c.key] || "—")}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const FeaturesPanel = () => {
  const [features, setFeatures] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setFeatures(r.data.features || {})).catch(() => setFeatures({}));
  }, []);

  const toggle = (key) => setFeatures((f) => ({ ...f, [key]: !(f[key] !== false) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/features", { features });
      toast.success("Sections updated — reload the site to see changes");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!features) return <p className="text-brand-textMuted">Loading…</p>;

  return (
    <div data-testid="features-panel">
      <h2 className="font-display font-bold text-2xl text-white mb-1">Sections & Features</h2>
      <p className="text-brand-textMuted text-sm mb-6">
        Turn parts of the site on or off. Disabled sections vanish from the menu, homepage and footer, and their pages redirect home. Perfect for starting lean and switching things on as you grow.
      </p>
      <div className="max-w-2xl space-y-2">
        {FEATURE_LABELS.map(([key, label, desc]) => {
          const on = features[key] !== false;
          return (
            <div key={key} className="flex items-center justify-between gap-4 bg-[#0C131D] border border-white/10 rounded-lg px-4 py-3" data-testid={`feature-row-${key}`}>
              <div className="min-w-0">
                <p className="text-white text-sm font-medium">{label}</p>
                <p className="text-brand-textMuted text-xs">{desc}</p>
              </div>
              <button
                onClick={() => toggle(key)}
                data-testid={`feature-toggle-${key}`}
                aria-pressed={on}
                className={`relative h-6 w-11 rounded-full transition-colors shrink-0 ${on ? "bg-brand-cyan" : "bg-white/15"}`}
              >
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${on ? "translate-x-[22px]" : "translate-x-0.5"}`} />
              </button>
            </div>
          );
        })}
      </div>
      <button onClick={save} disabled={saving} className="btn-primary px-5 py-2.5 text-sm mt-6 disabled:opacity-60" data-testid="features-save-btn">
        {saving ? "Saving…" : "Save Changes"}
      </button>
    </div>
  );
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState(location.pathname === "/admin/finance" ? "finance" : "videos");

  const doLogout = () => {
    logout();
    navigate("/admin", { replace: true });
  };

  return (
    <div className="min-h-screen bg-brand-base flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-white/10 bg-[#080C13] flex flex-col fixed inset-y-0">
        <div className="p-5 border-b border-white/10 flex items-center gap-3">
          <img src={IMAGES.logo} alt="logo" className="h-9 w-9 object-contain" />
          <div>
            <p className="text-white font-display font-bold text-sm leading-none">Project CMS</p>
            <p className="text-brand-textMuted text-xs mt-1">{user?.email}</p>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                tab === key ? "bg-brand-cyan/15 text-brand-cyan" : "text-brand-textSec hover:text-white hover:bg-white/5"
              }`}
            >
              <Icon size={17} /> {label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-1">
          <button onClick={() => navigate("/admin/giveaway")} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-brand-textSec hover:text-brand-cyan hover:bg-white/5" data-testid="admin-giveaway-link">
            <Gift size={17} /> Giveaway Wheel
          </button>
          <a href="/" target="_blank" rel="noreferrer" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-brand-textSec hover:text-white hover:bg-white/5" data-testid="admin-view-site">
            <ExternalLink size={17} /> View Site
          </a>
          <button onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-brand-textSec hover:text-red-400 hover:bg-white/5" data-testid="admin-logout-btn">
            <LogOut size={17} /> Log Out
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 ml-60 p-8 lg:p-10">
        {RESOURCES[tab] && <CrudManager config={RESOURCES[tab]} />}
        {tab === "finance" && <FinancePanel />}
        {tab === "sections" && <FeaturesPanel />}
        {tab === "settings" && <SettingsPanel />}
        {tab === "members" && (
          <ReadOnlyTable
            testid="members-table"
            endpoint="/admin/members"
            title="Community Members"
            columns={[
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "created_at", label: "Joined", render: (r) => new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) },
            ]}
          />
        )}
        {tab === "subscribers" && (
          <ReadOnlyTable
            testid="subscribers-table"
            endpoint="/admin/subscribers"
            title="Community Subscribers"
            columns={[
              { key: "email", label: "Email" },
              { key: "name", label: "Name" },
              { key: "created_at", label: "Joined", render: (r) => new Date(r.created_at).toLocaleDateString("en-GB") },
            ]}
          />
        )}
        {tab === "messages" && (
          <ReadOnlyTable
            testid="messages-table"
            endpoint="/admin/messages"
            title="Contact Messages"
            columns={[
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "subject", label: "Subject" },
              { key: "message", label: "Message", render: (r) => <span className="block max-w-md">{r.message}</span> },
              { key: "created_at", label: "Date", render: (r) => new Date(r.created_at).toLocaleDateString("en-GB") },
            ]}
          />
        )}
      </main>
    </div>
  );
}
